import type { ApolloError } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import type * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CONSTANTS } from '../../constants';
import { apiRoutes } from '../../constants/api-routes.constants';
import { SIGNUP_MUTATION } from '../../graphql/resolvers/userMutations/signUp.gql';
import { isSafeRedirectPath } from '../../helpers/isSafeRedirectPath';
import { userSignUpInitialState } from '../../initial-states/userSignUp.initialState';
import { SignUpSchema } from '../../yupValidationSchemas/signUpSchema';
import AuthHeader from '../AuthHeader/AuthHeader';
import ErrorBlock from '../ErrorBlock/ErrorBlock';
import LoginLink from '../UI/LoginLink';
import { SignUpFormGrid } from './index';
import './signup.scss';

const UserSignUp = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();

  const [createUser, { loading, error }] = useMutation(SIGNUP_MUTATION, {
    variables: { input: { ...userSignUpInitialState } },
    onCompleted: ({ createUser }) => {
      if (!createUser) return;
      // TRIL-239: gate ON → server returns a one-time Persona hosted-flow URL; hand off to it.
      // Gate OFF → no idvHostedUrl → proceed to login exactly as before.
      if (createUser.idvHostedUrl) {
        window.location.href = createUser.idvHostedUrl;
        return;
      }
      // TRIL-459: forward the `from` deep-link (set by the anonymous-reader
      // wall) through to /log-in so a brand-new account lands back on the
      // post. Can't survive the IDV branch above (external Persona redirect).
      const fromState = (location.state as { from?: unknown } | null)?.from;
      const from = isSafeRedirectPath(fromState) ? fromState : null;
      navigate(apiRoutes.LOGIN, from ? { state: { from } } : undefined);
    },
    onError: (ex: ApolloError) => {
      console.error(ex);
    },
  });

  return (
    <>
      {loading && (
        <div
          className="LinearProgress"
          role="progressbar"
          aria-label="Loading"
        />
      )}
      <div className="Signup__Grid">
        <div className="Signup__Wrapper">
          <AuthHeader
            header={CONSTANTS.USER.SIGNUP}
            link={apiRoutes.LOGIN}
            signup={true}
            styleName="Signup__Auth__HEADER"
          />
          <Formik
            initialValues={userSignUpInitialState}
            validationSchema={SignUpSchema}
            onSubmit={(values, actions) => {
              createUser({
                variables: { input: { ...values } },
              }).then(() => {
                actions.resetForm({ values: { ...userSignUpInitialState } });
              });
            }}
          >
            {(formikProps) => (
              <SignUpFormGrid
                disabled={error?.message as string}
                formikProps={formikProps}
              />
            )}
          </Formik>
        </div>
        <div className="Signup__Right_Col">
          {!error && (
            <LoginLink
              route={apiRoutes.LOGIN}
              text="Already have an account?"
            />
          )}
          {error && <ErrorBlock error={error} />}
        </div>
      </div>
    </>
  );
};

export default UserSignUp;
